<template>
  <div>

    <div class="title-section">
      <div class="display-1 font-weight-bold">{{meta.title}}</div>
      <div class="subheading">{{meta.subtitle}}</div>
    </div>


    <v-divider class="spacer"/><div class="headline font-weight-bold">Preface</div><v-divider class="div-after"/>
    <div class="body-1">This page covers some very high level aspects of
      my project. You can think of it as a very loose Software Development Plan 
      or Design Document, as this article covers much of the same information that
      would normally be captured by those documents. Here's how I'll be breaking 
      things down:
    </div>
    <card-list :items="intro_list"/>


    <v-divider class="spacer"/><div class="headline font-weight-bold">Domain Overview</div><v-divider class="div-after"/>
    <div class="body-1">
      Ray tracing is generally used as a way to achieve global illumination (direct and indirect
      lighting) when rendering a virtual scene. In the context of how light actually works, ray 
      tracing tries to account for the fact that real light bounces around rather than just hitting
      a surface and stopping. Rays essentially shoot out from light sources, bounce around the
      environment, and eventually make their way to the viewer. In practice, however, ray tracing 
      will usually source rays from the camera in order to cut out any unnecessary computations 
      for rays of light that don't make it to the viewer. You might hear this called "backwards"
      ray tracing, but as far as I know nobody really does it forwards! 
    </div>
    <div class="body-1">
      If we take a closer look at how ray tracing works, we're building an image pixel-by-pixel 
      by following independent rays into the scene. Some pretty cool effects can be generated with 
      ease by deciding how to respond to a "hit", or the case where our ray has intersected some 
      entity that exists in the scene, but we'll dig into that at a later date. This is a pretty
      important detail, however, as it opens the door to some performance improvements by
      parallelizing operations! 
    </div>
    <div class="body-1">
      OK! So we can assume that a ray tracer visually recreates some virtual scene, cool! We'll
      also need a way to display these results. If we decide to go the route of rendering a scene
      in <span>real-time</span>, we need to keep in mind that we're doing lots and lots of potentially 
      expensive calculations, so we'll need to pay close attention to ways in which we can improve 
      performance. We could also <span>export</span> our rendered image and view it later.
    </div>
    <div class="body-1">
      From these basic details, we can begin to construct a fairly simple domain model for our
      ray tracer, which will help us to decide on our scope and some key features/quality attributes
      for our system. There are a few entities in the diagram that may be more associated with the
      design of the solution, but given that the domain itself revolves around an algorithm I'm 
      pretty OK with that. 
    </div>
    <v-img class="u-image-large" :src="require(`../assets/updates/2/domain_model.png`)"/>
      <div class="font-weight-medium">Ray Tracing Domain Model</div>
    <div class="body-1">
      This model also assumes that we'll be transforming from world coordinates to camera 
      coordinates when running the ray tracer. I considered including screen coordinates,
      but I feel that that's less important considering ray tracing can either be done in 
      world coordinate or screen coordinates (the latter being preferred, unless you really
      want to convert your rays into world space using the inverse of your view transform). 
    </div>
    <div class="body-1">
      If you're interested in seeing how this might work from a dynamic context, here's a
      super simplified sequence diagram going over how a basic ray tracer might work in 
      some given context where an image must be synthesized and returned. It does not take 
      into account any performance concerns, and doesn't really assume much about what happens
      after we send a ray out into the scene. As far as we know right know, we shoot the ray out,
      (are any more generated? doesn't matter!) then we get some information back that we need 
      to process in order to determine the color of the given pixel.
    </div>
    <v-img class="u-image-large" :src="require(`../assets/updates/2/domain_sequence.png`)"/>
      <div class="font-weight-medium">Ray Tracer - High Level Sequence Diagram</div>


    <v-divider class="spacer"/><div class="headline font-weight-bold">Architecturally Significant Requirements</div><v-divider class="div-after"/>
    <div class="body-1">
      With a basic understanding of our domain, I think it's time to consider some 
      <span class="font-weight-bold">ASRs!</span> While 
      not every requirement here might be fulfilled, they're still going to have an impact 
      on architecture. 
    </div>
    <card-list :items="asr_list"/>


    <v-divider class="spacer"/><div class="headline font-weight-bold">Risk and Scope Management</div><v-divider class="div-after"/>
    <div class="body-1">
      There are a variety of <span class="font-weight-medium">checkpoints</span> throughout
      the semester where I'll need to deliver a image rendered by my raytracer. I'll also need 
      to deliver the final product to my professor (source code and all)! As a result, managing 
      risks related to <span class="font-weight-medium">on-time delivery</span> is essential. 
      This is pretty tangled up with <span class="font-weight-medium">scope</span>, considering 
      the number of enhancements and features that I want to deliver in addition to the MVP. 
    </div>
    <card-list :items="risk_list"/>
    <div class="body-1">
      So how can we nip these risks in the bud? While simpler said than done, approaching the 
      project with an MVP/enhancement mentality can keep things on track. As a result, the following
      features and quality attributes are categorized into MVP or enhancement. Structuring the project 
      in checkpoints should also help combat scope creep and gold plating. Choice of technology 
      will be key here, though I'm willing to take some risks as I want to learn something new. 
    </div>


    <v-divider class="spacer"/><div class="headline font-weight-bold">Features and Quality Attributes</div><v-divider class="div-after"/>
    <div class="body-1">
      Here's a list of some more specific features and qualities that I want my ray tracer 
      to have. This is a working list, so expect some things to change.
    </div>
    <div class="headline font-weight-bold">General Features</div>
      <card-list :items="features_list"/>
    <div class="headline font-weight-bold">Non real-time Features</div>
      <card-list :items="software_features"/>
    <div class="headline font-weight-bold">Real-time Features</div>
      <card-list :items="realtime_features"/>
    <div class="headline font-weight-bold">Quality Attributes</div>
      <card-list :items="qualities_list"/>
    

    <v-divider class="spacer"/><div class="headline font-weight-bold">Architecture Model</div><v-divider class="div-after"/>
    <div class="body-1">
      I'm interested in coming up with an architecture that will enable me to safely 
      deliver an MVP, while affording me the opportunity to delve into some of the 
      enhancements and concepts that deeply interest me outside of just the baseline. After 
      some brainstorming, I came up with a rough idea of how I want to structure some of 
      the more sigificant parts of my system. 
    </div>
    <v-img class="u-image-large" :src="require(`../assets/updates/2/architecture.png`)"/>
      <div class="font-weight-medium">Simple Overview of Architecture</div>
    <div class="body-1">
      This model also assumes that any given ray tracer would be able to access the same 
      data structures/scene definition. What isn't clear is how these data structures will 
      be defined and accessed. 
    </div>


    <v-divider class="spacer"/><div class="headline font-weight-bold">Choice of Technology</div><v-divider class="div-after"/>
    <div class="body-1">
      For this project, I'd like to stick to a language that I'm fairly comfortable with. I'd 
      also like to cut some corners on the amount of total effort where possible, so a more 
      fully featured library/framework would be preferred. Ultimately, I'll choose 
      a stack based on the following parameters:
    </div>
      <card-list :items="stack_params"/>
    <div class="body-1">
      After a bit of research, I came up with this list of options.
    </div>
      <card-list :items="stack_options"/>
    <div class="headline font-weight-bold">Java</div>
    <div class="body-1">
      I have to admit that I have some bias for <span class="font-weight-medium">Java</span>, 
      but this is the simplest option for a non-performant ray tracer that simply has to run 
      and produce an image, and there are quite a few options that I can pursue if and when 
      I decide to seek real-time performance. 
    </div>
    <div class="body-1">
      For <span class="font-weight-medium">displaying images</span> rendered by the ray tracer,
      there are quite a few options available. Using a <span class="font-weight-medium">BufferedImage</span>
      or <span class="font-weight-medium">VolatileImage</span> with Swing could allow for some 
      performant image rendering, regardless of the performance of the ray tracer. Images won't be
      transparent, so I'd probably end up using the latter option. If I decide to use JavaFX, I could
      use a <span class="font-weight-medium">WritableImage</span>. If this is a significant bottleneck
      to performance, however, I could consider using an OpenGL binding. <span class="font-weight-medium">
      LWJGL</span> is a great option, since it has support for OpenCL and manages user input as well. 
      It also seems to be using fairly recent OpenGL/CL versions, compared to something like LibGDX. 
    </div>
    <div class="body-1">
      Managing <span class="font-weight-medium">user input/interface</span> concerns should be 
      fairly simple with both Swing and JavaFX. However, LWJGL is another option for managing 
      user input. It doesn't seem to have great GUI support out of the box, and I'm a little 
      hesitant to mix heavyweight/lightweight components, but I suppose I could try and combine 
      LWJGL with Swing. 
    </div>
    <div class="body-1">
      I've taken a look at two options for <span class="font-weight-medium">OpenCL bindings</span>
      in Java; <span class="font-weight-medium">JOCL</span> and the afformentioned LWJGL. I'm 
      leaning towards the latter as its a little more fully featured, but the question becomes 
      do I really need those features? I'd like to think that LWJGL opens the door to future 
      additions/extensions onto the system, but that may not be as important a requirement 
      when considering how much time I have available for the project. 
    </div>
    <div class="body-1">
      I'd expect some <span class="font-weight-medium">performance</span> drops with Java, 
      but after doing some research I'm fairly comfortable with the effort required to get a 
      simple ray tracer to run in real-time using Java for the overarching app-logic. My 
      biggest concern would be (if I decide to use OpenCL) the performance of sending scene 
      data down to OpenCL and the performance of returning the image and then rendering it. 
      I could skip that last step and try to leverage OpenCL's shared data space with OpenGL, 
      but that's only like a solution for the real-time ray tracer. 
    </div>
    <div class="headline font-weight-bold">C++</div>
    <div class="body-1">
      C++ is my go-to for more performance intensive projects, and it's what I have the most 
      experience with in the context of computer graphics. For <span class="font-weight-medium">
      displaying images</span> rendered by the ray tracer,
      I'd probably go with OpenGL, or I'd look into what support SFML and QT have for rendering 
      images. I believe QT has an OpenGL widget I could also use to easily integrate OpenGL
      with a GUI. Which brings us to <span class="font-weight-medium">user input/interface</span> concerns! 
      OpenGL + GLFW is always an option, but I believe it'd be a bit simpler to use something like 
      SFML or QT for handling any UI and input concerns. Getting an <span class="font-weight-medium">
      OpenCL binding</span> for C++ isn't really much of a struggle!
    </div>
    <div class="body-1">
      I would expect general <span class="font-weight-medium">performance</span> with C++ to 
      be better when compared to Java, but at the end of the day I'll need to balance performance 
      with my (1) familiarity with frameworks, (2) the amount of effort required to get up and running 
      with one stack or the other,
    </div>
    <div class="headline font-weight-bold">Choice - Java!</div>
    <div class="body-1">
      I <span class="font-italic">tried</span> not to make it super obvious, but... I really like Java :)! 
      As for the specific choice of supporting libraries/frameworks, I'll decide between JavaFX and Swing 
      depending on the performance of image rendering. LWJGL will also come in handy when I start working 
      with OpenCL, and if I do end up needing to leverage OpenGL I can summon some evil spirits to get that
      working with Swing. 
    </div>
      


    <v-divider class="spacer"/><div class="headline font-weight-bold">Choice of Convention</div><v-divider class="div-after"/>
    <div class="body-1">
      Given how much OpenGL/CL crop up in this page, following their conventions seems like a smart idea. 
      As a result, I'll be following OpenGL conventions (post-multiplication, column major ordered matrices, 
      and handedness/coordinate system). 
    </div>


  </div>
</template>

<script>
import ImageDialog from '../components/ImageDialog';
import CardList from '../components/CardList';
import IconList from '../components/IconList';

export default {
  name: 'U_2',
  components: { ImageDialog, CardList, IconList, },
  data() {
    return {
      // UI State
      domain_dialog: false,

      // Lists 
      stack_params: [
        {title: 'Image Display', desc: 'Frameworks/libraries available for rendering the pixels that the ray tracer produces'},
        {title: 'User Interface and Input Support', desc: 'Ease of interface and input setup'},
        {title: 'OpenCL Bindings', desc: ''},
        {title: 'General Performance', desc: ''},
      ],
      stack_options: [
        {title: 'Java', desc: 'LWGJL (includes OpenGL and OpenCL bindings) + Swing/JavaFX'},
        {title: 'Java', desc: 'JOCL + Swing/JavaFX'},
        {title: 'C++', desc: 'SFML + OpenCL bindings'},
        {title: 'C++', desc: 'OpenGL + GLEW + GLFW + OpenCL bindings'},
        {title: 'C++', desc: 'QT + OpenCL bindings'},
      ],
      intro_list: [
        {title: 'Domain Overview', desc: 'Domain model, ray tracing overview', icon: 'fas fa-lightbulb'},
        {title: 'Architecturally Significant Requirements', desc: 'Key factors when considering architecture', icon: 'fas fa-star'},
        {title: 'Risk & Scope Management', desc: 'Some key risks and mitigation tactics', icon: 'fas fa-exclamation-circle'},
        {title: 'Features & Quality Attributes', desc: 'Functional/non-functional requirements', icon: 'fas fa-list-ul'},
        {title: 'Architecture Model', desc: 'Loose model dictating architecture', icon: 'fas fa-home'},
        {title: 'Choice of Technology', desc: 'Language, frameworks/libraries, etc...', icon: 'fas fa-code'},
        {title: 'Choice of Convention', desc: 'Coordinate system, row major/column major, pre/post multiply', icon: 'fas fa-th'},
      ],

      risk_list: [
        {title: 'Failure to deliver on time', desc: '', icon: 'fas fa-exclamation-circle'},
        {title: 'Scope creep', desc: '', icon: 'fas fa-exclamation-circle'},
        {title: 'Gold plating', desc: '', icon: 'fas fa-exclamation-circle'},
        {title: 'Unable to demo system in real-time', desc: '', icon: 'fas fa-exclamation-circle'},
        {title: 'Trouble with new tech!', desc: '', icon: 'fas fa-exclamation-circle'},
      ],

      features_list: [
        {title: 'Render 3D scene', desc: '', icon: 'fas fa-exclamation-circle', tooltip: 'Required for MVP'},
        {title: 'Basic entity support', desc: 'Spheres, planes, light sources, single camera', icon: 'fas fa-exclamation-circle', tooltip: 'Required for MVP'},
        {title: 'Adv. entity support', desc: 'Meshes, multiple cameras, additional primitives', icon: ''},
        {title: 'Basic texture support', desc: 'Procedural checkerboarding on planes', icon: 'fas fa-exclamation-circle', tooltip: 'Required for MVP'},
        {title: 'Adv. texture support', desc: 'Texture mapping, bump mapping, wrapping methods', icon: ''},
        {title: 'Save and load scenes', desc: 'Save modified scenes, load them dynamically', icon: 'fas fa-exclamation-circle', tooltip: 'Required for MVP'},
        {title: 'Adjust image resolution', desc: 'Adjust without closing/editing a config file', icon: 'fas fa-exclamation-circle', tooltip: 'Required for MVP'},
        {title: 'Modify renderer parameters', desc: 'Renderer should be parameterizable (number of samples, enabling/disabling effects)', icon: 'fas fa-exclamation-circle', tooltip: 'Required for MVP'},
        {title: 'Display scene details', desc: 'Entities, positions, etc...', icon: ''},
        {title: 'Text scene editing', desc: 'Edit scene without re-loading, text based', icon: ''},
        {title: '2D scene editor', desc: 'Edit scene without re-loading, using 2D UI', icon: ''},
        {title: 'Animation', desc: 'Animated entities (lights, meshes, etc...)', icon: ''},
      ],

      software_features: [
        {title: 'Output image as PNG', desc: '', icon: 'fas fa-exclamation-circle', tooltip: 'Required for MVP'},
        {title: 'Extra image formats', desc: 'JPG, video, PPM, etc...', icon: ''},
        {title: 'Change output format', desc: 'Implies non-default image formats', icon: ''},
        {title: 'Display progress', desc: 'Loading bar, text, etc...', icon: 'fas fa-exclamation-circle', tooltip: 'Required for MVP'},
        {title: 'Multi-threading', desc: 'Render pixels in parallel', icon: ''},
      ],

      realtime_features: [
        {title: 'Framerate counter', desc: 'Also display rendering state (is rendering?)', icon: ''},
        {title: 'Movable camera', desc: 'Move camera in real-time', icon: ''},
        {title: '3D scene editing', desc: 'More intuitive scene editing', icon: ''},
        {title: 'Animation Control', desc: 'Pause/play/restart animation states (entire scene, specific entities)', icon: ''},
      ],

      qualities_list: [
        {title: 'Render scene correctly', desc: '', icon: 'fas fa-exclamation-circle', tooltip: 'Required for MVP'},
        {title: 'Reasonable real-time performance', desc: '15 - 30 fps', icon: ''},
        {title: 'Error recovery', desc: 'Should be able to recover from basic errors', icon: 'fas fa-exclamation-circle', tooltip: 'Required for MVP'},
        {title: 'Usability', desc: 'Somewhat intuitive UI', icon: 'fas fa-exclamation-circle', tooltip: 'Required for MVP'},
        {title: 'Extensible scene framework', desc: '', icon: 'fas fa-exclamation-circle', tooltip: 'Required for MVP'},
        {title: 'Support for multiple renderers', desc: 'Real-time, software/not real-time', icon: 'fas fa-exclamation-circle', tooltip: 'Required for MVP'},
      ],

      asr_list: [ //Performance, real-time, visualization, mobility, availability, extensibility, stuff like that
        { title: 'Time and other work', desc: 'This one is most important for me. I can\'t exactly spend every '
        + 'ounce of effort and time that I have on this project. As a result, more ambitious requirements are '
        + 'inherently at risk of failure, which puts the entire system at jeopardy if it cannot operate without them.' },
        { title: 'Image quality', desc: 'Aside from time, image quality is the second most important requirement. '
        + 'There\'s no doubt that image quality can impact the architecture of the renderer itself, though ' 
        + 'it doesn\'t necessarily impact anything outside of the renderer (aside from the feasability of real-time).'},
        { title: 'Real-time performance', desc: 'Achieving real-time performance will require many aspects '
        + 'of the system to be designed carefully with performance in mind. The use of spatial data structures '
        + 'and hardware acceleration can help improve performance, and the choice of language/framework becomes '
        + 'far more important. ' },
        { title: 'Decoupled visualization', desc: 'I\'ll get into this requirement a little '
        + 'more in the next section. Essentially, our ray tracer simply takes in scene data as input and returns '
        + 'an image. We\' benefit from the decoupling of the ray tracer and whatever actually renders the image '
        + 'it returns. It could enable us to switch dynamically between real-time and not-real-time rendering! ' },
        { title: 'Extensible scene definition', desc: 'It might be nice to add in additional primitives at some '
        + 'point, as well different kinds of lights and scene entities. ' },
        { title: 'Dynamic scenes (animation)', desc: 'A not-so-important but still pretty cool enhancement for me '
        + 'would be to incorporate dynamic entities into the real-time renderer, or even to support the export of '
        + 'video from the software renderer!' },
      ],
      
    };
  },
  computed: {
    meta() {
      return this.$store.state.updates[2];
    },
  }
};
</script>